/* eslint-disable max-len */
import React, { useState } from 'react';
import {
  Row, Col, Tag, Button, Modal, Form, Input, Select, message,
} from 'antd';
import Config from '../../../../config';

const { Option } = Select;

// Your live Fiverr gigs. Add the remaining ones from your profile here as needed.
const services = [
  {
    id: 'urdf-sim',
    category: 'Programming & Tech',
    title: 'URDF file & simulation of any robot',
    desc: 'I will model your robot as a URDF and bring it to life in Gazebo / Ignition with accurate kinematics, inertials and physics.',
    rating: '5.0',
    reviews: 11,
    price: 75,
    accent: '#304CFD',
    fiverr: 'https://www.fiverr.com/munn33b/create-urdf-file-and-simulation-of-any-robot',
  },
  {
    id: 'rl-agent',
    category: 'AI Integrations',
    title: 'Single & multi-agent reinforcement learning',
    desc: 'Custom single-agent or multi-agent RL algorithms for your environment — from MDP formulation through training and evaluation.',
    rating: '4.4',
    reviews: 8,
    price: 90,
    accent: '#0F8B8D',
    fiverr: 'https://www.fiverr.com/munn33b/create-single-agent-and-multi-agent-reinforcement-learning-algorithm',
  },
  {
    id: 'drone-sim',
    category: 'AI & Integrations',
    title: 'Drone simulation with ArduPilot / PX4 in ROS 2 & Gazebo',
    desc: 'Full SITL drone simulation using ArduPilot or PX4, integrated with ROS 2 and Gazebo (gz-sim) for realistic flight testing.',
    rating: '5.0',
    reviews: null,
    price: null,
    accent: '#FF965D',
    fiverr: 'https://www.fiverr.com/munn33b/create-simulation-of-any-drone-with-ardupilot-or-px4-in-ros2-and-gz-sim',
  },
];

const cardStyle = {
  border: '2px solid #F5F8F9',
  borderRadius: '15px',
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

const ServicesContent = () => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const openOrder = (service) => {
    setActive(service);
    form.setFieldsValue({ service: service.title });
    setOpen(true);
  };

  const closeOrder = () => {
    setOpen(false);
    form.resetFields();
  };

  const onFinish = async (values) => {
    setSubmitting(true);
    try {
      const response = await fetch(Config.orderFormUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });
      if (response.ok) {
        message.success('Thanks! Your order request has been sent. I will get back to you shortly.');
        closeOrder();
      } else {
        message.error('Something went wrong sending your request. Please try again, or order on Fiverr.');
      }
    } catch (error) {
      message.error('Could not reach the server. Please try again later, or order on Fiverr.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <p style={{ marginBottom: '6px' }}>
        I work as a Level 2 seller on Fiverr (4.8&#9733; over 24 reviews) on robotics simulation,
        Sim2Real and reinforcement-learning projects. Pick a service below to send an order request,
        or buy directly on Fiverr. Prefer hourly? My rate is $35/hour.
      </p>
      <p style={{ marginBottom: '24px' }}>
        <a href="https://www.fiverr.com/munn33b" target="_blank" rel="noopener noreferrer">
          See all my services on Fiverr &rarr;
        </a>
      </p>

      <Row gutter={[20, 20]}>
        {services.map((s) => (
          <Col xs={24} sm={24} md={12} lg={8} key={s.id}>
            <div style={cardStyle}>
              <div style={{
                background: s.accent, color: '#fff', padding: '18px 18px 16px', minHeight: '96px',
              }}
              >
                <Tag color="rgba(255,255,255,0.18)" style={{ border: 'none', color: '#fff' }}>{s.category}</Tag>
                <h3 style={{ color: '#fff', margin: '10px 0 0', fontSize: '1.05rem' }}>{s.title}</h3>
              </div>
              <div style={{
                padding: '18px', display: 'flex', flexDirection: 'column', flex: 1,
              }}
              >
                <p style={{ color: '#404040', fontSize: '0.9rem', flex: 1 }}>{s.desc}</p>
                <div style={{ margin: '6px 0 14px', color: '#44566C', fontSize: '0.85rem' }}>
                  <span style={{ color: '#FF965D' }}>&#9733; </span>
                  {s.rating}
                  {s.reviews ? ` (${s.reviews})` : ''}
                  <span style={{ float: 'right', fontWeight: 600 }}>
                    {s.price ? `From $${s.price}` : 'Custom quote'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button type="primary" shape="round" style={{ background: '#304CFD', borderColor: '#304CFD' }} onClick={() => openOrder(s)}>
                    Order
                  </Button>
                  <Button shape="round" href={s.fiverr} target="_blank" rel="noopener noreferrer">
                    Buy on Fiverr
                  </Button>
                </div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      <Modal
        title={active ? `Order: ${active.title}` : 'Order a service'}
        visible={open}
        onCancel={closeOrder}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item name="service" label="Service">
            <Input disabled />
          </Form.Item>
          <Form.Item name="name" label="Your name" rules={[{ required: true, message: 'Please enter your name.' }]}>
            <Input placeholder="Full name" />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Please enter a valid email.' }]}>
            <Input placeholder="you@example.com" />
          </Form.Item>
          <Form.Item name="budget" label="Budget (USD)">
            <Select placeholder="Select a range" allowClear>
              <Option value="< $50">Less than $50</Option>
              <Option value="$50 - $100">$50 - $100</Option>
              <Option value="$100 - $250">$100 - $250</Option>
              <Option value="$250+">$250+</Option>
              <Option value="Hourly ($35/hr)">Hourly ($35/hr)</Option>
            </Select>
          </Form.Item>
          <Form.Item name="details" label="Project details" rules={[{ required: true, message: 'Please describe your project.' }]}>
            <Input.TextArea rows={5} placeholder="Tell me about your robot, environment, timeline and what you need delivered." />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" shape="round" htmlType="submit" loading={submitting} style={{ background: '#304CFD', borderColor: '#304CFD' }}>
              Send order request
            </Button>
            {active && (
              <Button type="link" href={active.fiverr} target="_blank" rel="noopener noreferrer">
                or buy on Fiverr
              </Button>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ServicesContent;
